/**
 * What a rendered figure is a picture of, and what the render stage refused.
 *
 * A worksheet too dense to read in one image is rendered as an **overview** plus cell-range
 * **tiles**: a 52-inch sheet's 10 pt text lands at 2.7 px on the model's 1568 px edge, and a model
 * asked to read it invents box labels while reporting the image illegible. So the identity of a
 * render is structure, not a sentence — the describe prompt, the description budget, the outline
 * and the popover all select on it, and the label a person or a model reads is *built* from these
 * fields and never parsed back into them.
 *
 * Both halves live in `@repo/shared` because the worker's response, the Convex row and the
 * browser's read-out are three sides of one vocabulary (the worker's own copy is
 * `FigureRenderRecord` / `PageRenderDrop` in `apps/document-processor/app/models.py`).
 */
/**
 * Which view of a sheet a render is.
 *
 * `sheet` is a whole sheet rendered as one image — the only kind that existed before tiling, and
 * still what a sheet legible in one image gets. A sheet that is not splits into `sheet_overview`
 * (the whole extent, for layout and navigation rather than for reading) plus `sheet_tile`s.
 */
export const FIGURE_RENDER_KINDS = ["sheet", "sheet_overview", "sheet_tile"];
/**
 * Why the render stage had a view and did not ship it.
 *
 * - `render_cap` — the document's render budget was spent. The workbook's URL pool is consumed
 *   overview-first in sheet order, so what this names is the tail.
 * - `byte_budget` — the shared render byte pool was spent before the count was.
 * - `time_budget` — the render stage ran out of wall clock.
 * - `render_failed` — the renderer produced nothing for this view.
 * - `overview_too_wide` — the sheet is too wide for one fitted page to hold at all, so it ships
 *   as tiles with nothing showing it whole. The opposite of the others: the tiles survived.
 */
export const PAGE_RENDER_DROP_REASONS = [
    "render_cap",
    "byte_budget",
    "time_budget",
    "render_failed",
    "overview_too_wide",
];
/**
 * The one place a rendered view is turned into words.
 *
 * Every surface that names a view — the derived artifact a figure hangs off, the label the
 * describe prompt quotes, the outline entry, the citation, the popover — builds it here from the
 * record. A label is never parsed back into identity: `tileIndex` is a number on the row, not a
 * digit somebody recovers from a string, and a surface that needs the range reads `cellRange`.
 *
 * `sheet` keeps the wording a whole-sheet render has always had, so a sheet legible in one image
 * reads exactly as it did.
 */
export function formatRenderLabel(render) {
    const view = formatRenderView(render);
    if (!view)
        return `Sheet: ${render.sheetName}`;
    return `${render.sheetName} — ${view}, ${formatCellRangeExtent(render.cellRange)}`;
}
/**
 * Which of a sheet's views this is, without the sheet or the extent around it.
 *
 * A citation already names the sheet and the range from the block's own locators, and repeating
 * them would make the label say each twice; what it is missing is that the picture shows part of
 * the sheet rather than the whole of it. A whole-sheet render answers nothing, because there is
 * no other view of that sheet to distinguish it from.
 */
export function formatRenderView(render) {
    if (render.kind === "sheet")
        return undefined;
    if (render.kind === "sheet_overview")
        return "overview";
    if (render.tileIndex === undefined)
        return "tile";
    return render.tileCount === undefined
        ? `tile ${render.tileIndex}`
        : `tile ${render.tileIndex} of ${render.tileCount}`;
}
/** `A1:BE61` → `columns A–BE, rows 1–61`; anything that does not parse is quoted as it came. */
function formatCellRangeExtent(cellRange) {
    const match = /^([A-Za-z]+)(\d+):([A-Za-z]+)(\d+)$/.exec(cellRange.trim());
    if (!match)
        return cellRange;
    const [, rawFirstColumn, firstRow, rawLastColumn, lastRow] = match;
    if (!rawFirstColumn || !firstRow || !rawLastColumn || !lastRow)
        return cellRange;
    const firstColumn = rawFirstColumn.toUpperCase();
    const lastColumn = rawLastColumn.toUpperCase();
    const columns = firstColumn === lastColumn ? `column ${firstColumn}` : `columns ${firstColumn}–${lastColumn}`;
    const rows = firstRow === lastRow ? `row ${firstRow}` : `rows ${firstRow}–${lastRow}`;
    return `${columns}, ${rows}`;
}
/**
 * Which slice of a long description this is, in the one form every surface says it in.
 *
 * A workbook view's transcription does not fit the block cap, so it is stored as consecutive
 * blocks; the phrase that distinguishes them is written once here because the block text carries
 * it, the citation carries it, and the two saying it differently would read as two facts.
 */
export function formatDescriptionPartLabel(partIndex, partCount) {
    return `part ${partIndex} of ${partCount}`;
}
/** A rendered view and which part of its description a block or a citation is naming. */
export function formatRenderPartLabel(render, partIndex, partCount) {
    return `${formatRenderLabel(render)} · ${formatDescriptionPartLabel(partIndex, partCount)}`;
}
